import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Navigate, useNavigate } from "react-router-dom";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";

import { useAuth } from "auth/AuthContext";
import { getToken } from "auth/storage";
import { getTokenExpiryMs } from "auth/token";

const ProtectedRoute = ({ children }) => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [isExpired, setIsExpired] = useState(false);
  const token = getToken();

  useEffect(() => {
    if (!token) return undefined;

    const expiry = getTokenExpiryMs(token);
    if (!expiry) {
      // Undecodable / no expiry — treat as expired.
      setIsExpired(true);
      return undefined;
    }

    const timeout = expiry - Date.now();
    if (timeout <= 0) {
      setIsExpired(true);
      return undefined;
    }

    const timer = setTimeout(() => setIsExpired(true), timeout);
    return () => clearTimeout(timer);
  }, [token]);

  const handleLogout = () => {
    logout();
    setIsExpired(false);
    navigate("/authentication", { replace: true });
  };

  if (!token) {
    return <Navigate to="/authentication" replace />;
  }

  return (
    <>
      {children}

      <Dialog open={isExpired} onClose={handleLogout}>
        <DialogTitle>Session Expired</DialogTitle>
        <DialogContent>Your session has expired. Please sign in again to continue.</DialogContent>
        <DialogActions>
          <Button variant="contained" color="primary" onClick={handleLogout}>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

ProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ProtectedRoute;
